import {FormEvent, useState} from 'react';
import {Link} from 'react-router-dom';
import {useConnectedApps, useDisconnectApp, useTokenMutations, useTokens} from '../api/tokens';
import ErrorBox from '../components/feedback/ErrorBox';
import {SkeletonList} from '../components/feedback/Skeleton';
import Icon from '../components/Icon';
import {confirm} from '../hooks/useConfirm';
import {toast} from '../hooks/useToast';
import {formatDate} from '../lib/dates';
import {ApiToken} from '../types';
import Button from '../components/Button';

// More → Assistants: connect Claude, ChatGPT, Claude Code or a script to your garden. Chat apps
// go through OAuth (/authorize) and show up as connected apps; everything else uses a personal
// token, shown once here. Both can be cut off from this page.
// Fixed, not window.location.origin: OAuth tokens are bound to this exact address.
const MCP_URL = 'https://sowandtell.garden/mcp';

const copy = async (text: string, what: string) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.success(`${what} copied.`);
    } catch {
        toast.problem('Could not copy. Select it and copy by hand.');
    }
};

export default function AssistantsPage() {
    const tokens = useTokens();
    const apps = useConnectedApps();
    const {create, revoke} = useTokenMutations();
    const disconnect = useDisconnectApp();
    const [name, setName] = useState('');
    const [fresh, setFresh] = useState<{token: string; item: ApiToken} | null>(null);

    const mint = (e: FormEvent) => {
        e.preventDefault();
        create.mutate(name.trim(), {
            onSuccess: result => {
                setFresh(result);
                setName('');
            }
        });
    };

    const cut = async (token: ApiToken) => {
        const done = await confirm({
            title: `Revoke “${token.name}”?`,
            body: 'Anything using it stops working straight away.',
            help: 'Make a new token any time; the old one cannot be brought back.',
            confirmLabel: 'Revoke',
            busyLabel: 'Revoking…',
            destructive: true,
            action: () => revoke.mutateAsync(token.id)
        });
        if (done) {
            toast.success('Revoked.');
            if (fresh?.item.id === token.id) setFresh(null);
        }
    };

    const claudeCodeCommand = (token: string) => `claude mcp add --transport http sowandtell ${MCP_URL} --header "Authorization: Bearer ${token}"`;

    return (
        <div className="settle-in flex flex-col gap-6">
            <header className="pt-2">
                <Link to="/more" className="meta mb-1 inline-flex items-center gap-1 no-underline">
                    <Icon name="back" size={16} /> More
                </Link>
                <h1 className="display m-0">Assistants</h1>
                <p className="meta mt-1">Let an assistant read and write your garden diary as you: ask what you did last spring, or tell it what you planted today.</p>
            </header>

            <section className="flex flex-col gap-2">
                <span className="band-label text-ink-2">Chat apps</span>
                <div className="card flex flex-col gap-2 p-4">
                    <p className="m-0 text-sm">
                        In Claude or ChatGPT, add a custom connector with this address and sign in when asked. No client id or secret is needed.
                    </p>
                    <button type="button" className="field flex items-center justify-between gap-2 text-left font-mono text-sm" onClick={() => void copy(MCP_URL, 'Address')}>
                        <span className="truncate">{MCP_URL}</span>
                        <Icon name="share" size={18} className="shrink-0 text-ink-2" />
                    </button>
                </div>
                {apps.isPending && <SkeletonList rows={1} />}
                {apps.isError && <ErrorBox error={apps.error} onRetry={() => void apps.refetch()} />}
                {apps.data && apps.data.length > 0 && (
                    <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface">
                        {apps.data.map(app => (
                            <div key={app.id} className="flex min-h-[56px] items-center gap-3 px-4 py-2">
                                <Icon name="leaf" size={20} className="text-shoot-text" />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate font-semibold">{app.clientName}</span>
                                    <span className="meta block truncate">
                                        {app.clientDomain ? `${app.clientDomain} · ` : ''}connected {formatDate(app.createdAt)}
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    className="text-sm font-semibold text-rust underline-offset-2 hover:underline"
                                    onClick={async () => {
                                        const done = await confirm({
                                            title: `Disconnect ${app.clientName}?`,
                                            body: 'It will have to sign in again to reach your garden.',
                                            confirmLabel: 'Disconnect',
                                            busyLabel: 'Disconnecting…',
                                            destructive: true,
                                            action: () => disconnect.mutateAsync(app.id)
                                        });
                                        if (done) toast.success('Disconnected.');
                                    }}
                                >
                                    Disconnect
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                {apps.data && apps.data.length === 0 && <p className="meta m-0 px-1">No chat app is connected yet.</p>}
            </section>

            <section className="flex flex-col gap-2">
                <span className="band-label text-ink-2">Tokens</span>
                <p className="meta m-0 px-1">For Claude Code, Cursor, scripts and anything that takes a bearer token. Each token acts as you until you revoke it.</p>

                {fresh && (
                    <div className="nudge flex flex-col gap-2">
                        <span className="font-semibold">Your new token. Copy it now; it will not be shown again.</span>
                        <button type="button" className="field break-all text-left font-mono text-sm" onClick={() => void copy(fresh.token, 'Token')}>
                            {fresh.token}
                        </button>
                        <div className="flex flex-wrap gap-2">
                            <Button variant="secondary" onClick={() => copy(fresh.token, 'Token')}>
                                Copy token
                            </Button>
                            <Button variant="secondary" onClick={() => copy(claudeCodeCommand(fresh.token), 'Claude Code command')}>
                                Copy Claude Code command
                            </Button>
                        </div>
                    </div>
                )}

                <form onSubmit={mint} className="flex gap-2">
                    <input className="field flex-1" placeholder="Name it: Claude Code on the laptop" value={name} maxLength={40} onChange={e => setName(e.target.value)} />
                    <Button type="submit" busy={create.isPending} busyLabel="Making…" disabled={name.trim() === ''}>
                        New token
                    </Button>
                </form>
                {create.isError && <ErrorBox error={create.error} />}

                {tokens.isPending && <SkeletonList rows={2} />}
                {tokens.isError && <ErrorBox error={tokens.error} onRetry={() => void tokens.refetch()} />}
                {tokens.data && tokens.data.length > 0 && (
                    <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface">
                        {tokens.data.map(token => (
                            <div key={token.id} className="flex min-h-[56px] items-center gap-3 px-4 py-2">
                                <Icon name="hash" size={20} className="text-ink-2" />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate font-semibold">
                                        {token.name} <span className="meta font-mono">…{token.hint}</span>
                                    </span>
                                    <span className="meta block truncate">
                                        made {formatDate(token.createdAt)}
                                        {token.lastUsedAt ? ` · last used ${formatDate(token.lastUsedAt)}` : ' · never used'}
                                    </span>
                                </span>
                                <button type="button" className="text-sm font-semibold text-rust underline-offset-2 hover:underline" onClick={() => void cut(token)}>
                                    Revoke
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section className="flex flex-col gap-2">
                <span className="band-label text-ink-2">What an assistant can do</span>
                <p className="meta m-0 px-1">
                    Everything you can: read and search your tells, add and edit them (with photos from a link), manage places, and export. It sees only your garden, and only while a token or connection is live.
                </p>
            </section>
        </div>
    );
}
